import { useEffect, useRef, useState } from "react";
import { Pencil, Target } from "lucide-react";

const DesiredScoreCard = ({ desiredScore: initialDesiredScore }) => {
  const [desiredScore, setDesiredScore] = useState(initialDesiredScore ?? null);
  const [tempScore, setTempScore] = useState("");
  const [showPicker, setShowPicker] = useState(false);

  const popupRef = useRef(null);

  useEffect(() => {
    setDesiredScore(initialDesiredScore ?? null);
  }, [initialDesiredScore]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPicker]);

  const openPicker = () => {
    setTempScore(desiredScore ?? "");
    setShowPicker(true);
  };

  const handleSetScore = async (event) => {
    event.preventDefault();

    const numericScore = Number(tempScore);
    if (!numericScore || numericScore < 10 || numericScore > 90) return;

    try {
      await fetch("/api/user-preferences", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ desiredScore: numericScore }),
      });

      setDesiredScore(numericScore);
      setTempScore("");
      setShowPicker(false);
    } catch (err) {
      console.error("Failed to save desired score");
    }
  };

  const isValidScore =
    tempScore !== "" && Number(tempScore) >= 10 && Number(tempScore) <= 90;

  return (
    <div
      className="group relative min-h-[144px] cursor-pointer rounded-md border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-md"
      onClick={() => !showPicker && openPicker()}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold uppercase text-gray-500">
            Desired Score
          </p>
          <p className="mt-4 text-2xl font-extrabold text-gray-800">
            {desiredScore ?? "N/A"}
          </p>
        </div>
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-slate-100 text-purple-700">
          <Target className="h-5 w-5" aria-hidden="true" />
        </span>
      </div>

      <button
        type="button"
        aria-label="Edit desired score"
        aria-haspopup="dialog"
        className="absolute bottom-4 right-4 grid h-8 w-8 place-items-center rounded-md text-gray-400 transition hover:bg-purple-50 hover:text-purple-700 group-hover:text-purple-700"
        onClick={(event) => {
          event.stopPropagation();
          openPicker();
        }}
      >
        <Pencil className="h-4 w-4" aria-hidden="true" />
      </button>

      {showPicker && (
        <div
          ref={popupRef}
          className="absolute left-1/2 top-full z-50 mt-3 w-64 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-md border border-slate-200 bg-white p-4 shadow-xl"
          onClick={(event) => event.stopPropagation()}
        >
          <form onSubmit={handleSetScore}>
            <label
              htmlFor="desired-score"
              className="block text-xs font-semibold uppercase text-gray-500"
            >
              Target score
            </label>
            <input
              id="desired-score"
              type="number"
              min="10"
              max="90"
              placeholder="10 to 90"
              value={tempScore}
              onChange={(event) => setTempScore(event.target.value)}
              className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-center text-sm text-gray-700 outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
            />

            <button
              type="submit"
              disabled={!isValidScore}
              className="mt-3 w-full rounded-md bg-gradient-to-r from-fuchsia-900 to-fuchsia-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:ring-2 hover:ring-purple-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Set Score
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default DesiredScoreCard;
